import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { translations, SUPPORTED_LANGUAGES } from './translations';

/**
 * App-wide internationalization. The single source of truth for the active
 * language is `localStorage.cwm_settings_v1.locale.language` — the same key
 * the Settings screen writes. Any tab (or any iframe) changing that key
 * instantly re-renders every consumer via the `storage` event listener.
 *
 * Usage:
 *
 *   import { useI18n } from '../../features/i18n';
 *   const { t, lang, setLang } = useI18n();
 *   <h1>{t('nav.discover')}</h1>
 *
 * String resolution:
 *   1. key in translations[lang]
 *   2. key in translations.en  (fallback)
 *   3. the key itself           (last-resort fallback so missing copy
 *      surfaces visibly during development)
 *
 * Keys are dot-paths so files stay organized by surface (nav.*, settings.*,
 * chat.*, common.*). Missing-key fallback never throws.
 */

const I18N_STORAGE_KEY = 'cwm_settings_v1';
const DEFAULT_LANG = 'en';

const I18nContext = createContext({
  t: (k) => k,
  lang: DEFAULT_LANG,
  setLang: () => {},
});

function readLang() {
  try {
    const raw = localStorage.getItem(I18N_STORAGE_KEY);
    if (!raw) return DEFAULT_LANG;
    const parsed = JSON.parse(raw);
    const l = parsed?.locale?.language;
    if (l && SUPPORTED_LANGUAGES.includes(l)) return l;
    return DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

function writeLang(lang) {
  try {
    const raw = localStorage.getItem(I18N_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = {
      ...parsed,
      locale: { ...(parsed.locale || {}), language: lang },
    };
    localStorage.setItem(I18N_STORAGE_KEY, JSON.stringify(next));
    // Fire a storage event in the same tab (the native `storage` event only
    // fires in *other* tabs) so listeners elsewhere in this app re-render.
    window.dispatchEvent(new CustomEvent('cwm-lang-change', { detail: lang }));
  } catch {}
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(readLang);

  // Sync across tabs + same-tab Settings writes
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== I18N_STORAGE_KEY) return;
      setLangState(readLang());
    };
    const onCustom = (e) => setLangState(e.detail || readLang());
    window.addEventListener('storage', onStorage);
    window.addEventListener('cwm-lang-change', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('cwm-lang-change', onCustom);
    };
  }, []);

  const setLang = (next) => {
    if (!SUPPORTED_LANGUAGES.includes(next)) return;
    writeLang(next);
    setLangState(next);
    // Update <html lang="..."> for a11y + browser spellcheck
    try { document.documentElement.lang = next; } catch {}
  };

  // Keep the <html lang="..."> attribute in sync with the active language
  // on mount and any time the lang changes.
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch {}
  }, [lang]);

  const t = useMemo(() => makeTranslator(lang), [lang]);

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

/**
 * Resolve a dot-path key against a given language dict, falling back to
 * English, then to the key itself. Supports interpolation via {name} tokens.
 *
 *   t('common.greeting', { name: 'Alex' })
 *     // dict: { common: { greeting: 'Hello, {name}!' } }
 *     // => 'Hello, Alex!'
 */
function makeTranslator(lang) {
  const primary = translations[lang] || translations[DEFAULT_LANG];
  const fallback = translations[DEFAULT_LANG];
  return function t(key, params) {
    if (!key) return '';
    const value = resolve(primary, key) ?? resolve(fallback, key) ?? key;
    if (!params) return value;
    return String(value).replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? String(params[k]) : `{${k}}`));
  };
}

function resolve(dict, key) {
  if (!dict) return undefined;
  const parts = key.split('.');
  let ref = dict;
  for (const p of parts) {
    if (ref == null || typeof ref !== 'object') return undefined;
    ref = ref[p];
  }
  return typeof ref === 'string' ? ref : undefined;
}

/**
 * Convenience helper for non-component code (e.g. callbacks defined outside
 * React's render tree). Reads the current language directly.
 */
export function tStatic(key, params) {
  return makeTranslator(readLang())(key, params);
}

/**
 * Return the English display name of the given language code. Useful for
 * building the "Please respond in X" preamble sent to the AI.
 */
export function languageEnglishName(code) {
  const names = {
    en: 'English', es: 'Spanish', fr: 'French', pt: 'Portuguese',
    it: 'Italian', de: 'German', ja: 'Japanese', zh: 'Chinese',
  };
  return names[code] || 'English';
}
